import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { hasPermission } from '../utils/permissions';

const HomePage = ({ user, userInfo }) => {
  // Restricted users only see the sections a Founder/Super User checked for them.
  const can = (key) => hasPermission(userInfo, key);
  const navigate = useNavigate();
  const [showGrantManagementModal, setShowGrantManagementModal] = useState(false);
  const [pendingTaskCount, setPendingTaskCount] = useState(0);
  const hasPendingTasks = pendingTaskCount > 0;
  // Fresh-from-server official name overrides for the User Information card.
  // The userInfo prop is captured at login and doesn't refresh after the user
  // edits the company name in the Company Info / Formation form.
  const [freshOfficialName, setFreshOfficialName] = useState(null);
  // Company logo state
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoActionsOpen, setLogoActionsOpen] = useState(false);
  const canManageLogo = !!userInfo && userInfo.accountType !== 'investor';

  useEffect(() => {
    if (!userInfo) return;
    (async () => {
      try {
        const r = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/logo`);
        if (r.ok) {
          const d = await r.json();
          setLogoUrl(d.logoUrl || null);
        }
      } catch (e) { console.error('Error loading company logo:', e); }
    })();
  }, [userInfo]);

  const handleLogoFile = async (file) => {
    if (!file) return;
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/logo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (r.ok) {
        const d = await r.json();
        setLogoUrl(d.logoUrl || null);
      } else {
        alert('Failed to upload logo.');
      }
    } catch (e) {
      console.error(e);
      alert('Failed to upload logo.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Delete the current company logo?')) return;
    try {
      const r = await api.delete(`${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/logo`);
      if (r.ok) setLogoUrl(null);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    console.log(typeof user);
    console.log("userInfo:", userInfo);
    if (!userInfo) return;
    // Refetch the user's company so a name change on Company Info / Formation
    // form shows up immediately when the user comes back to the home page.
    (async () => {
      try {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/auth/check`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        if (resp.ok) {
          const data = await resp.json();
          setFreshOfficialName(data.selectedCompanyOfficialName || data.companyOfficialName || null);
        }
      } catch (e) { console.error('Error refreshing /me on home page:', e); }
    })();
  }, [user, userInfo])

  // Compute the number of remaining todolist tasks for the indicator badge.
  // All formation tasks (the "Formation Tasks" section) collapse to 1, while
  // each pending monthly task / custom admin task counts as 1.
  useEffect(() => {
    if (!userInfo) return;

    const isAdmin = userInfo.accountType === 'admin' || userInfo.accountType === 'superadmin';
    const isFounder = userInfo.accountType === 'founder' || userInfo.accountType === 'client';

    const isPending = (status) => status && status !== 'completed' && status !== 'skipped' && status !== 'not_applicable';

    const computeCount = async () => {
      let count = 0;
      try {
        // Look up monthly cycle settings: if the cycle hasn't started yet,
        // there are no monthly task instances to count.
        let cycleStartMonth = null;
        try {
          const cycleResp = await fetch(
            `${process.env.REACT_APP_BACKEND_API_URL}/month-status/monthly-cycle-settings`,
            { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } }
          );
          if (cycleResp.ok) {
            const cycleData = await cycleResp.json();
            cycleStartMonth = cycleData.cycleStartMonth || null;
          }
        } catch (e) {
          console.error('Error fetching monthly cycle settings:', e);
        }

        // Compute the number of monthly cycle periods that exist now (each is one task instance).
        const monthsSinceCycleStart = () => {
          if (!cycleStartMonth) return 0;
          const [y, m] = cycleStartMonth.split('-').map(Number);
          const now = new Date();
          const months = (now.getFullYear() - y) * 12 + (now.getMonth() + 1 - m) + 1;
          return Math.max(0, months);
        };

        if (isAdmin) {
          // Formation tasks: count whole section as 1 if anything pending
          const tasksResp = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/tasks`);
          if (tasksResp.ok) {
            const data = await tasksResp.json();
            const tasks = data.tasks || data || [];
            if (Array.isArray(tasks) && tasks.some(t => isPending(t.status))) count += 1;
            console.log("formation tasks count:", count, "tasks:", tasks);
          }
          // Custom admin tasks: one-off pending tasks count once; repeating
          // monthly tasks count once per cycle period since cycle start, minus
          // completions. If the cycle hasn't started yet, skip the repeats.
          const customResp = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks`);
          if (customResp.ok) {
            const customData = await customResp.json();
            const customTasks = customData.tasks || customData || [];
            const completions = customData.completions || [];
            const cycleMonths = monthsSinceCycleStart();
            // Only count custom tasks whose assignedTo matches the admin role.
            const adminMatches = (t) => {
              const a = (t.assignedTo || '').toLowerCase();
              return a === '' || a === 'admin' || a === 'both' || a === 'all';
            };
            if (Array.isArray(customTasks)) {
              for (const t of customTasks) {
                if (!adminMatches(t)) continue;
                const isRepeated = t.isRepeated === 'true' || t.isRepeated === true;
                if (isRepeated) {
                  if (cycleMonths === 0) continue;
                  const completedForTask = completions.filter(c => c.taskId === t.id && c.status === 'completed').length;
                  const remaining = Math.max(0, cycleMonths - completedForTask);
                  count += remaining;
                  console.log("counting repeated task", t, "cycleMonths:", cycleMonths, "completedForTask:", completedForTask, "remaining:", remaining);
                } else if (t.status === 'active' || (isPending(t.status) && t.status !== 'completed')) {
                  count += 1;
                  console.log("counting one-off task", t, "status:", t.status);
                }
              }
            }
            
          }
        } else if (isFounder) {
          // Formation tasks: 1 if any pending
          const founderResp = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/company-formation/founder/tasks`);
          if (founderResp.ok) {
            const data = await founderResp.json();
            const tasks = Array.isArray(data) ? data : (data.tasks || []);
            if (Array.isArray(tasks) && tasks.some(t => isPending(t.status))) count += 1;
          }
          // Monthly transaction tasks: only count if the cycle has started.
          if (cycleStartMonth) {
            try {
              const unconfirmedMonthsResp = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/expense/months-with-unconfirmed`,
                { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } }
              );
              if (unconfirmedMonthsResp.ok) {
                const monthsData = await unconfirmedMonthsResp.json();
                count += (monthsData.months || []).length;
              }
            } catch (e) {
              console.error('Error fetching monthly task count:', e);
            }
          }
        }
      } catch (err) {
        console.error('Error computing pending task count:', err);
      }
      setPendingTaskCount(count);
    };

    computeCount();
  }, [userInfo]);


  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto p-6">
        {/* User Information Section */}
        {userInfo && (
          <div className="card p-6 mb-6">
            <div className="flex gap-6 items-stretch">
              {/* Logo Box - top-left, large vertical box */}
              <div className="flex-shrink-0 w-48">
                {logoUrl ? (
                  <div className="relative h-full min-h-[140px] bg-white border border-gray-200 rounded-lg overflow-hidden flex items-center justify-center p-3">
                    <img src={logoUrl} alt="Company logo" className="max-w-full max-h-full object-contain" />
                    {canManageLogo && (
                      <button
                        type="button"
                        onClick={() => setLogoActionsOpen(o => !o)}
                        className="absolute top-2 right-2 px-2 py-1 text-xs bg-white/90 border border-gray-200 rounded shadow-sm hover:bg-gray-50"
                        title="Manage logo"
                      >
                        Manage
                      </button>
                    )}
                    {canManageLogo && logoActionsOpen && (
                      <div className="absolute top-10 right-2 z-20 bg-white border border-gray-200 rounded-md shadow-md py-1 text-sm min-w-[140px]">
                        <label className="block px-3 py-1.5 text-gray-700 hover:bg-gray-50 cursor-pointer">
                          {logoUploading ? 'Uploading...' : 'Replace logo'}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={logoUploading}
                            onChange={(e) => {
                              const f = e.target.files && e.target.files[0];
                              if (f) handleLogoFile(f);
                              e.target.value = '';
                              setLogoActionsOpen(false);
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => { setLogoActionsOpen(false); handleLogoDelete(); }}
                          disabled={logoUploading}
                          className="block w-full text-left px-3 py-1.5 text-red-600 hover:bg-gray-50"
                        >
                          Delete logo
                        </button>
                      </div>
                    )}
                  </div>
                ) : canManageLogo ? (
                  <label
                    className={`relative h-full min-h-[140px] rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 flex flex-col items-center justify-center text-blue-600 ${logoUploading ? 'opacity-50' : 'cursor-pointer hover:bg-blue-100 hover:border-blue-400'}`}
                    title="Upload a company logo"
                  >
                    <svg className="w-10 h-10 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-semibold">Upload Logo</span>
                    <span className="text-xs text-blue-500 mt-1">Click to add company logo</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={logoUploading}
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        if (f) handleLogoFile(f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                ) : (
                  <div className="h-full min-h-[140px] bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-sm text-gray-400">No logo</span>
                  </div>
                )}
              </div>

              {/* Right side: User Information header + info cards */}
              <div className="flex-1 flex flex-col justify-center">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">User Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Name</p>
                        <p className="text-sm text-gray-900">{userInfo.name || userInfo.email || userInfo.username || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Company</p>
                        <p className="text-sm text-gray-900">{freshOfficialName || userInfo.selectedCompanyOfficialName || userInfo.companyOfficialName || userInfo.selectedCompany || userInfo.companyName || 'Not assigned'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Account Type</p>
                        <p className="text-sm text-gray-900">
                          {userInfo.accountType === 'superadmin' ? 'Super Admin' :
                           userInfo.accountType === 'admin' ? 'Admin' :
                           userInfo.accountType === 'super_user' ? 'Super User' :
                           userInfo.accountType === 'founder' ? 'Founder' :
                           userInfo.accountType === 'client' ? 'Client' : ""}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Layout with Navigation Panel on the left */}
        <div className="flex gap-6">
          {/* Navigation Panel - Company Info and Registrations (Left Side) */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden sticky top-6">
              <div className="space-y-1 p-2">
                {/* Company Info */}
                {can('company_info') && (
                <button
                  onClick={() => navigate('/company-info')}
                  className="w-full p-3 hover:bg-gray-50 transition-colors text-left group rounded-lg"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center group-hover:bg-cyan-200 transition-colors">
                        <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-semibold text-gray-900">Company Info</p>
                      <p className="text-xs text-gray-500">View details</p>
                    </div>
                  </div>
                </button>
                )}

                {/* Registrations */}
                {can('registrations') && (
                <button
                  onClick={() => navigate('/registrations')}
                  className="w-full p-3 hover:bg-gray-50 transition-colors text-left group rounded-lg"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                        <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-semibold text-gray-900">Registrations</p>
                      <p className="text-xs text-gray-500">Manage</p>
                    </div>
                  </div>
                </button>
                )}
              </div>
            </div>
          </div>

          {/* StreamLaunch Platform Card (Right/Main Area) */}
          <div className="flex-1">
            <div className="card p-8 mb-6">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">StreamLaunch Platform</h1>
                <p className="text-lg text-gray-600">Access all your business tools and services</p>
              </div>

              {/* Founder Dashboard Button - Prominent Row (for founders only) */}
              {userInfo?.accountType === 'founder' && (
                <div className="mb-6 max-w-4xl mx-auto">
                  <button
                    onClick={() => navigate('/client-todolist')}
                    className="w-full p-6 rounded-lg hover:shadow-lg transition-all duration-200 text-left group border-2 border-indigo-500 relative"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 relative">
                          <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center group-hover:bg-indigo-400 transition-all duration-200">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                          {hasPendingTasks && (
                            <span
                              className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 bg-red-500 rounded-full border-2 border-white flex items-center justify-center text-[11px] font-bold text-white"
                              title={`You have ${pendingTaskCount} pending task${pendingTaskCount === 1 ? '' : 's'}`}
                            >
                              {pendingTaskCount > 99 ? '99+' : pendingTaskCount}
                            </span>
                          )}
                        </div>
                        <div className="ml-4">
                          <p className="text-lg font-semibold text-gray-900">Todolist</p>
                          <p className="text-sm text-gray-600">Track your company formation progress and complete setup tasks</p>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-gray-500 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </button>
                </div>
              )}

          {/* Admin Company Formation Dashboard Button - Prominent Row (for admins only) */}
          {(userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin') && (
            <div className="mb-6 max-w-4xl mx-auto">
              <button
                onClick={() => navigate('/admin/todolist')}
                className="w-full p-6 text-white rounded-lg hover:shadow-lg transition-all duration-200 text-left group border-2 border-indigo-500 relative"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 relative">
                      {hasPendingTasks && (
                        <span
                          className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 bg-red-500 rounded-full border-2 border-white z-10 flex items-center justify-center text-[11px] font-bold text-white"
                          title={`There are ${pendingTaskCount} pending task${pendingTaskCount === 1 ? '' : 's'}`}
                        >
                          {pendingTaskCount > 99 ? '99+' : pendingTaskCount}
                        </span>
                      )}
                      <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center group-hover:bg-indgo-400 transition-all duration-200">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 003.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 713.138-3.138z" />
                        </svg>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-lg font-semibold text-gray-900">Todolist</p>
                      <p className="text-sm text-gray-500">Manage incomplete company tasks</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-gray-500 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {/* Grant Management Button (replaces Grant Submission, Grant Budget, Unconfirmed Bank Statements, Confirmed Transactions) */}
            {can('grants') && (
            <button
              onClick={() => setShowGrantManagementModal(true)}
              className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-medium transition-shadow duration-200 text-left group h-full min-h-[90px]"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors duration-200">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Grant Submission & Management</p>
                  <p className="text-xs text-gray-500">Manage grants and transactions</p>
                </div>
              </div>
            </button>
            )}

            {(userInfo?.isSuperUser || userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin' ) && (
              <button
                onClick={() => navigate('/user-management')}
                className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-medium transition-shadow duration-200 text-left group h-full min-h-[90px]"
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors duration-200">
                      <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"/>
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">User Management</p>
                    <p className="text-xs text-gray-500">Manage company users</p>
                  </div>
                </div>
              </button>
            )}


            {/* Investor Portal Button - Available to Everyone */}
            {can('data_room') && (
            <button
              onClick={() => navigate('/investor-portal')}
              className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-medium transition-shadow duration-200 text-left group h-full min-h-[90px]"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center group-hover:bg-pink-200 transition-colors duration-200">
                    <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Data Room and Investor Portal</p>
                  <p className="text-xs text-gray-500">Documents, team info, and investor materials</p>
                </div>
              </div>
            </button>
            )}

            {/* Legal Documents Button */}
            {can('legal_documents') && (
            <button
              onClick={() => navigate('/legal-documents')}
              className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-medium transition-shadow duration-200 text-left group h-full min-h-[90px]"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors duration-200">
                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Legal Documents</p>
                  <p className="text-xs text-gray-500">Access company legal documents</p>
                </div>
              </div>
            </button>
            )}

            {/* Board Meeting Minutes Button */}
            {can('board_minutes') && (
            <button
              onClick={() => navigate('/board-meeting-minutes')}
              className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-medium transition-shadow duration-200 text-left group h-full min-h-[90px]"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors duration-200">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Board Meeting Minutes</p>
                  <p className="text-xs text-gray-500">View and upload meeting minutes</p>
                </div>
              </div>
            </button>
            )}

            {/* Cap Table Button */}
            {can('cap_table') && (
            <button
              onClick={() => navigate('/cap-table')}
              className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-medium transition-shadow duration-200 text-left group h-full min-h-[90px]"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 transition-colors duration-200">
                    <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6h13M9 17H4V5a2 2 0 012-2h7l5 5v3M9 17v4m0-4h11m-7 4h7a2 2 0 002-2v-2a2 2 0 00-2-2h-7" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Cap Table</p>
                  <p className="text-xs text-gray-500">Manage company capitalization</p>
                </div>
              </div>
            </button>
            )}

            {/* Personnel Button */}
            {can('personnel') && (
            <button
              onClick={() => navigate('/personnel')}
              className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-medium transition-shadow duration-200 text-left group h-full min-h-[90px]"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 transition-colors duration-200">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Personnel</p>
                  <p className="text-xs text-gray-500">View and manage personnel</p>
                </div>
              </div>
            </button>
            )}

            {/* Task Documents Button - for admins */}
            {(userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin') && (
              <button
                onClick={() => navigate('/task-documents')}
                className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-medium transition-shadow duration-200 text-left group h-full min-h-[90px]"
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center group-hover:bg-teal-200 transition-colors duration-200">
                      <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Task Documents</p>
                    <p className="text-xs text-gray-500">View uploaded task files</p>
                  </div>
                </div>
              </button>
            )}
          </div>
        </div>
          </div>
        </div>
      </div>

      {/* Grant Management Modal */}
      {showGrantManagementModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowGrantManagementModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Grant Submission & Management</h2>
              <button
                onClick={() => setShowGrantManagementModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {/* Grant Submissions Portal */}
              <button
                onClick={() => {
                  setShowGrantManagementModal(false);
                  navigate('/grant-submission');
                }}
                className="p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-medium transition-all duration-200 text-left group"
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors duration-200">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-base font-semibold text-gray-900 group-hover:text-blue-600">Grant Submissions Portal</p>
                    <p className="text-sm text-gray-500 mt-1">Create and submit your grant applications</p>
                  </div>
                </div>
              </button>

              {/* Grant and Fund Financials */}
              <button
                onClick={() => {
                  setShowGrantManagementModal(false);
                  navigate('/grant-fund-financials');
                }}
                className="p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:shadow-medium transition-all duration-200 text-left group"
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center group-hover:bg-indigo-200 transition-colors duration-200">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-base font-semibold text-gray-900 group-hover:text-indigo-600">Financial Management</p>
                    <p className="text-sm text-gray-500 mt-1">Import and manage financial data</p>
                  </div>
                </div>
              </button>

              {/* Grant-Related Documents */}
              <button
                onClick={() => {
                  setShowGrantManagementModal(false);
                  navigate('/grant-related-documents');
                }}
                className="p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-teal-500 hover:shadow-medium transition-all duration-200 text-left group"
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center group-hover:bg-teal-200 transition-colors duration-200">
                      <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-base font-semibold text-gray-900 group-hover:text-teal-600">Grant-Related Documents</p>
                    <p className="text-sm text-gray-500 mt-1">Progress Reports, Research Integrity Assurance, etc.</p>
                  </div>
                </div>
              </button>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
